import React, { createContext, useCallback, useContext, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import MuiDialogTitle from "@mui/material/DialogTitle";
import styled from "@emotion/styled";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { css } from "@emotion/react";

const DialogContext = createContext();

// ---------------- Styled Title ----------------
const StyledDialogTitle = styled((props) => {
  const { children, onClose, ...other } = props;
  return (
    <MuiDialogTitle {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose && (
        <IconButton aria-label="close" onClick={onClose} size="large">
          <CloseIcon />
        </IconButton>
      )}
    </MuiDialogTitle>
  );
})`
  & .MuiTypography-root {
    display: inline;
  }

  & .MuiIconButton-root {
    position: absolute;
    right: 8px;
    top: 8px;
  }

  ${({ theme }) => css`
    .MuiTypography-root {
      color: ${theme.palette.primary.main};
    }

    .MuiIconButton-root {
      color: ${theme.palette.secondary.main};
    }
  `}
`;

// ---------------- Custom Dialog ----------------
const CustomDialog = ({ dialogId, title, content, onClose, maxWidth, height }) => {
  return (
    <Dialog
      disableEnforceFocus
      disableEscapeKeyDown
      open={Boolean(dialogId)}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
    >
      <StyledDialogTitle onClose={onClose}>{title}</StyledDialogTitle>

      <DialogContent style={{ height: height, width: "auto" }}>
        {content()} {/* IMPORTANT: CALL FUNCTION */}
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Provider ----------------
const DialogProvider = ({ children }) => {
  const [dialogs, setDialogs] = useState([]);

  // OPEN DIALOG — stores content as function → preserves state
  const openDialog = (dialogId, title, content, maxWidth = "sm", height = "auto") => {
    setDialogs((prev) => [
      ...prev.filter((d) => d.dialogId !== dialogId), // avoid duplicates
      {
        dialogId,
        title,
        content: () => content, // <-- This keeps form state!
        maxWidth,
        height,
      },
    ]);
  };

  // CLOSE DIALOG
  const unSetDialog = (dialogId) => {
    setDialogs((prev) => prev.filter((d) => d.dialogId !== dialogId));
  };

  return (
    <DialogContext.Provider value={{ openDialog, unSetDialog }}>
      {children}

      {dialogs.map((dialog) => (
        <CustomDialog
          key={dialog.dialogId}
          dialogId={dialog.dialogId}
          title={dialog.title}
          content={dialog.content}
          onClose={() => unSetDialog(dialog.dialogId)}
          maxWidth={dialog.maxWidth}
          height={dialog.height}
        />
      ))}
    </DialogContext.Provider>
  );
};

const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return context;
};

export { DialogProvider, useDialog };
